/* **** NOTE TO DEVELOPER **** 

There is no check yet to ensure the admin is not setting their own role to support.
If an admin sets their own email's role to support, they will be unable to make any changes or
view their projects and the server will throw a 403 error. This will require you to navigate into 
the database itself and set the role back to admin. Until we are able to check this, do not set the 
role for the email you test the site with as "support".

*/

/*
As an admin of an organization in Love Big, I want to manage my users and their current roles,
so I can ensure only certain accounts have the correct access to certain information.
*/

//Packages
import React, { useCallback, useState, useEffect, useContext } from "react";
import axios from "axios";
//Components
import User from "../../components/User";
//Material UI Components
import {
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
//Context
import { userContext } from "../../Context";
//API Route
import { Route } from "../../utils/config";

export default function Users() {
  const { user } = useContext(userContext);

  //List of users
  const [userList, setUserList] = useState([]);

  //New user state, updates if admin inputs info for new user
  const [newUser, setNewUser] = useState({
    email: "",
    role: "Support",
  });

  //Gets list of accounts associated with current partner (organization)
  const getPartnerAccounts = useCallback(() => {
    let store = JSON.parse(localStorage.getItem("login"));
    axios
      .get(`${Route}/Partners/GetPartnerAccountList`, {
        params: {
          partnerId: user.partnerId,
        },
        headers: { Authorization: `Bearer ${store.token}` },
      })
      .then((res) => {
        setUserList(res.data);
      })
      .catch((err) => {
        throw err;
      });
  }, [user.partnerId]);

  //Saves account to partner based on email
  const savePartnerAccount = (email, role) => {
    let store = JSON.parse(localStorage.getItem("login"));
    const body = {
      partnerId: user.partnerId,
      accountEmail: email,
      role: role,
    };
    axios
      .post(`${Route}/Partners/SavePartnerAccount`, JSON.stringify(body), {
        headers: {
          Authorization: `Bearer ${store.token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        setUserList([...userList, res.data]);
      })
      .catch((err) => {
        throw err;
      });
  };

  //Checks if user is connected to the partner through email, then adds them to
  // the partner if they do not.
  const handleNewAccount = (email, role) => {
    let canAddUser = true;

    if (email) {
      userList.forEach((user) => {
        if (user.email === email) {
          canAddUser = false;
          return;
        }
      });
      if (canAddUser) {
        savePartnerAccount(email, role);
      }
    }
  };

  const removeUser = (userToRemove) => {
    const params = {
      partnerId: user.partnerId,
      accountEmail: userToRemove.email,
      role: userToRemove.role,
    };
    let store = JSON.parse(localStorage.getItem("login"));
    axios
      .post(`${Route}/Partners/RemovePartnerAccount`, JSON.stringify(params), {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${store.token}`,
        },
      })
      .then((res) => {
        setUserList(
          userList.filter((user) => {
            return user.id !== userToRemove.id;
          })
        );
      })
      .catch((err) => {
        throw err;
      });
  };

  useEffect(() => {
    getPartnerAccounts();
  }, [getPartnerAccounts]);

  return (
    <Grid container item xs={12} mt={4}>
      <Grid item xs={12} mx={4}>
        <Grid container item xs={12} mb={4} justifyContent="center">
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleNewAccount(newUser.email, newUser.role);
            }}
          >
            <Grid container direction="row" spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                ></TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="role">User Role</InputLabel>
                  <Select
                    labelId="role"
                    label="User Role"
                    defaultValue="Support"
                    value={newUser.role}
                    name="projectTypeId"
                    onChange={(e) =>
                      setNewUser({ ...newUser, role: e.target.value })
                    }
                  >
                    <MenuItem value={"Admin"}>Admin</MenuItem>
                    <MenuItem value={"Support"}>Support</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid container item xs={12} md={4} justifyContent={"center"}>
                <Button variant="contained" color="secondary" type="submit">
                  + Add User
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {userList.map((userItem) => {
            return (
              <User
                id={userItem.id}
                userId={userItem.id}
                firstName={userItem.firstName}
                lastName={userItem.lastName}
                email={userItem.email}
                role={userItem.role}
                handleRoleChange={savePartnerAccount}
                removeUser={removeUser}
                curUserRole={user.role}
              ></User>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
}
